import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, App, Descriptions, Form, Modal, Upload, type FormInstance } from 'antd'
import { ScanLine } from 'lucide-react'
import { ocrFileSchema } from '@super-mega/server/contracts/ocr'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import type { LegalEntityFormValues } from './legal-entity-form.model.ts'

export function LegalRepresentativeOcr({ form }: { form: FormInstance<LegalEntityFormValues> }) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const recognize = useMutation(trpc.ocr.idCard.mutationOptions())
  const [candidate, setCandidate] = useState<{ name?: string | null; idNumber?: string | null }>()
  const runOcr = async (file: File) => {
    const parsed = ocrFileSchema.safeParse(file)
    if (!parsed.success) {
      void message.error(parsed.error.issues[0]?.message || '请选择有效身份证图片')
      return
    }
    setCandidate(undefined)
    const data = new FormData()
    data.set('file', file)
    try {
      const result = await recognize.mutateAsync(data)
      if (!result.name && !result.idNumber) {
        void message.warning('未识别到姓名或身份证号，请上传身份证人像面，或手动填写')
        return
      }
      setCandidate({ name: result.name, idNumber: result.idNumber })
    } catch (error) {
      void message.error(getErrorMessage(error, '身份证识别失败，请重试或手动填写'))
    }
  }
  return (
    <>
      <Form.Item label="身份证原件（选填）" className="mb-0!">
        <Upload
          accept="image/jpeg,image/png,image/bmp"
          disabled={recognize.isPending}
          multiple={false}
          showUploadList={false}
          listType="picture-card"
          beforeUpload={(file) => {
            void runOcr(file)
            return false
          }}
        >
          <button
            className="text-content-muted flex cursor-pointer flex-col items-center gap-1 border-0 bg-transparent p-0 text-xs disabled:cursor-not-allowed"
            disabled={recognize.isPending}
            type="button"
            aria-label="识别法人身份证"
          >
            <ScanLine aria-hidden className="size-5" />
            {recognize.isPending ? '识别中…' : '选择身份证'}
          </button>
        </Upload>
        <p className="text-content-soft mt-2 mb-0 text-xs">上传人像面后自动识别，确认结果后填入法人信息。</p>
        <p className="text-content-muted mt-2 mb-0 text-xs">支持 JPEG、PNG、BMP，最大 10 MiB。</p>
      </Form.Item>
      <Modal
        title="核对法人身份证识别结果"
        open={Boolean(candidate)}
        onCancel={() => setCandidate(undefined)}
        okText="应用到表单"
        onOk={() => {
          if (!candidate) {
            return
          }
          form.setFieldsValue({
            ...(candidate.name ? { legalRepresentative: candidate.name } : {}),
            ...(candidate.idNumber ? { legalRepresentativeIdNumber: candidate.idNumber.toUpperCase() } : {})
          })
          setCandidate(undefined)
          void message.success('已填入法人信息，请核对后保存主体')
        }}
      >
        <Alert className="mb-4" type="info" showIcon title="仅填入识别到的字段，未识别的字段保留原内容。" />
        <Descriptions
          column={1}
          items={[
            { key: 'name', label: '法人姓名', children: candidate?.name || '未识别' },
            { key: 'idNumber', label: '法人身份证号', children: candidate?.idNumber || '未识别' }
          ]}
        />
      </Modal>
    </>
  )
}
